/**
 * CustomerHistoryPanel — Tailor Dashboard Phase 2.
 *
 * Compact luxury memory surface for one customer. Designed as an additive panel
 * inside the order details modal. Aesthetic constraints:
 *   • No CRM-style data tables.
 *   • Soft ivory cards · gold accents · serif headings · generous whitespace.
 *   • Lazy-loads on first expand to avoid extra round-trips per dashboard render.
 *
 * Data source: GET /api/tailor/customer-history/{customer_id}
 *   → fit_memory · orders · snapshots_by_category · alterations
 *
 * Action: POST /api/tailor/alteration-memory/{order_id}  (save fitting note)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { tailorAPI } from '../../api';
import {
  Sparkles, History, Scissors, ChevronDown, ChevronUp, Plus,
  CheckCircle, Loader2, Ruler, ShieldCheck,
} from 'lucide-react';
import { humanize } from '../../data/terminology';

const GOLD = '#D4AF37';
const INK = '#0F0F0F';
const MUTED = '#8a8a8a';
const LINE = '#E5E5E5';
const SOFT = '#FAF9F6';
const GREEN = '#166534';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const titleCase = (s) =>
  String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ALTERATION_TYPES = [
  { v: 'bust_tight',          l: 'Bust tightened' },
  { v: 'bust_loose',          l: 'Bust eased' },
  { v: 'waist_tight',         l: 'Waist tightened' },
  { v: 'waist_loose',         l: 'Waist eased' },
  { v: 'sleeve_short',        l: 'Sleeve shortened' },
  { v: 'sleeve_long',         l: 'Sleeve lengthened' },
  { v: 'shoulder_correction', l: 'Shoulder corrected' },
  { v: 'length_short',        l: 'Length shortened' },
  { v: 'length_long',         l: 'Length lengthened' },
  { v: 'other',               l: 'Other' },
];

const FitMemoryCard = ({ memory }) => {
  if (!memory) return null;
  const empty = (memory.order_count || 0) === 0;
  return (
    <div data-testid="fit-memory-card"
      className="p-3.5 mb-3 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(229,178,185,0.06))',
        border: `1px solid rgba(212,175,55,0.35)`,
      }}>
      <div className="flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: GOLD }} strokeWidth={1.6} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-[0.18em] uppercase mb-1" style={{ color: GOLD }}>
            AI Tailoring Memory
          </p>
          <p className="text-[12px] leading-snug" style={{ color: INK, fontWeight: 500 }}>
            {memory.summary || 'No prior tailoring data yet.'}
          </p>
          {!empty && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-[9.5px] px-1.5 py-0.5"
                style={{ backgroundColor: 'rgba(15,15,15,0.05)', color: INK }}>
                {memory.order_count} {memory.order_count === 1 ? 'order' : 'orders'}
              </span>
              {memory.preferred_fit && (
                <span className="text-[9.5px] px-1.5 py-0.5 capitalize"
                  style={{ backgroundColor: 'rgba(212,175,55,0.10)', color: '#7a6420' }}>
                  Prefers {memory.preferred_fit} fit
                </span>
              )}
              {memory.alteration_count > 0 && (
                <span className="text-[9.5px] px-1.5 py-0.5"
                  style={{ backgroundColor: 'rgba(22,101,52,0.08)', color: GREEN }}>
                  {memory.alteration_count} alteration{memory.alteration_count === 1 ? '' : 's'} on file
                </span>
              )}
            </div>
          )}
          {memory.recurring_issues?.length > 0 && (
            <p className="text-[10px] mt-1.5" style={{ color: MUTED }}>
              <span className="uppercase tracking-[0.10em] text-[9px]">Recurring · </span>
              {memory.recurring_issues.map((t) => titleCase(t)).join(' · ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const OrderTimeline = ({ orders, currentOrderId }) => {
  if (!orders || orders.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <History className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.6} />
        <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED }}>
          Order History
        </p>
      </div>
      <div className="relative pl-4">
        {/* Vertical thread */}
        <div className="absolute top-1 bottom-1 left-[5px] w-px" style={{ backgroundColor: LINE }} />
        {orders.map((o, i) => {
          const isCurrent = o.order_id === currentOrderId;
          return (
            <div key={o.order_id || i} className="relative pb-3 last:pb-0"
              data-testid={`history-order-${o.order_id}`}>
              <div
                className="absolute -left-[14px] top-1 w-2.5 h-2.5 rounded-full"
                style={{
                  backgroundColor: isCurrent ? GOLD : '#FFF',
                  border: `1px solid ${isCurrent ? GOLD : LINE}`,
                  boxShadow: isCurrent ? '0 0 0 3px rgba(212,175,55,0.18)' : 'none',
                }}
              />
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-[11.5px]" style={{ color: INK, fontWeight: isCurrent ? 600 : 500 }}>
                  {titleCase(o.category) || 'Order'}
                  {isCurrent && (
                    <span className="ml-1.5 text-[8.5px] tracking-[0.14em] uppercase px-1 py-0.5"
                      style={{ backgroundColor: GOLD, color: '#FFF' }}>
                      This order
                    </span>
                  )}
                </p>
                <p className="text-[9.5px] flex-shrink-0" style={{ color: MUTED }}>
                  {formatDate(o.created_at)}
                </p>
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: MUTED }}>
                {o.suggested_size && <>Size <span style={{ color: INK }}>{o.suggested_size}</span> · </>}
                {o.fit_pref && <>{titleCase(o.fit_pref)} fit · </>}
                {titleCase(o.status)}
                {typeof o.fit_confidence === 'number' && (
                  <> · <span style={{ color: o.fit_confidence >= 80 ? GREEN : GOLD }}>{o.fit_confidence}%</span> conf.</>
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SnapshotPills = ({ snapshots }) => {
  const [openCat, setOpenCat] = useState(null);
  if (!snapshots || snapshots.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Ruler className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.6} />
        <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED }}>
          Saved Measurements
        </p>
      </div>
      <div className="space-y-1.5">
        {snapshots.map((snap) => {
          const open = openCat === snap.category;
          const fields = Object.entries(snap.measurements || {})
            .filter(([k, v]) => typeof v === 'number' && humanize(k) !== null);
          return (
            <div key={snap.category}
              data-testid={`snapshot-${snap.category}`}
              className="border" style={{ borderColor: LINE, backgroundColor: '#FFF' }}>
              <button
                onClick={() => setOpenCat(open ? null : snap.category)}
                className="w-full px-3 py-2 flex items-center justify-between"
                style={{ backgroundColor: open ? SOFT : '#FFF' }}>
                <div className="text-left">
                  <p className="text-[11.5px] capitalize" style={{ color: INK, fontWeight: 500 }}>
                    {titleCase(snap.category)}
                  </p>
                  <p className="text-[9.5px]" style={{ color: MUTED }}>
                    Captured {formatDate(snap.captured_at)} · {fields.length} fields
                  </p>
                </div>
                {open
                  ? <ChevronUp  className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />
                  : <ChevronDown className="w-3.5 h-3.5" style={{ color: MUTED }} strokeWidth={1.5} />}
              </button>
              {open && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 px-3 py-2.5 border-t"
                  style={{ borderColor: LINE, backgroundColor: SOFT }}>
                  {fields.map(([k, v]) => (
                    <div key={k} className="flex items-baseline justify-between gap-2">
                      <span className="text-[10.5px]" style={{ color: MUTED }}>{humanize(k)}</span>
                      <span className="text-[11.5px]"
                        style={{ color: INK, fontFamily: 'Playfair Display, serif' }}>
                        {v} cm
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AlterationStream = ({ alterations }) => {
  if (!alterations || alterations.length === 0) return null;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Scissors className="w-3 h-3" style={{ color: GOLD }} strokeWidth={1.6} />
        <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: MUTED }}>
          Alteration Memory
        </p>
      </div>
      <div className="space-y-1.5">
        {alterations.map((a, i) => (
          <div key={i} data-testid={`alteration-${i}`}
            className="px-3 py-2 border" style={{ borderColor: LINE, backgroundColor: '#FFF' }}>
            <div className="flex items-baseline justify-between gap-2">
              <p className="text-[11.5px]" style={{ color: INK, fontWeight: 500 }}>
                {titleCase(a.type)}
                {typeof a.delta_cm === 'number' && a.delta_cm !== 0 && (
                  <span className="ml-1.5 text-[10px]" style={{ color: GOLD }}>
                    {a.delta_cm > 0 ? '+' : ''}{a.delta_cm} cm
                  </span>
                )}
              </p>
              <p className="text-[9.5px] flex-shrink-0" style={{ color: MUTED }}>
                {formatDate(a.created_at)}
              </p>
            </div>
            {a.category && (
              <p className="text-[9.5px] capitalize" style={{ color: MUTED }}>
                {titleCase(a.category)}
              </p>
            )}
            {a.note && (
              <p className="text-[10.5px] mt-1" style={{ color: INK, lineHeight: 1.45 }}>
                “{a.note}”
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const AddAlterationForm = ({ orderId, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bust_tight');
  const [delta, setDelta] = useState('');   // local string state — same architecture as Height/Weight
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const digitsAndSign = (s) => String(s).replace(/[^0-9-]/g, '').replace(/(?!^)-/g, '').slice(0, 4);

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const body = { type };
      const parsed = delta === '' || delta === '-' ? null : parseInt(delta, 10);
      if (parsed !== null && !Number.isNaN(parsed)) body.delta_cm = parsed;
      if (note.trim()) body.note = note.trim();
      await tailorAPI.saveAlteration(orderId, body);
      setSavedAt(Date.now());
      setNote(''); setDelta('');
      onSaved && onSaved();
      // Auto-close after a beat
      setTimeout(() => setOpen(false), 900);
    } catch (e) {
      // silent — keep form open for retry
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button data-testid="add-alteration-btn"
        onClick={() => setOpen(true)}
        className="w-full py-2.5 text-[10.5px] tracking-[0.14em] uppercase flex items-center justify-center gap-1.5"
        style={{ border: `1px dashed ${GOLD}`, color: '#7a6420', backgroundColor: 'rgba(212,175,55,0.04)' }}>
        <Plus className="w-3 h-3" strokeWidth={1.8} /> Log Fitting Alteration
      </button>
    );
  }

  return (
    <div className="p-3 border" style={{ borderColor: GOLD, backgroundColor: 'rgba(212,175,55,0.04)' }}
      data-testid="alteration-form">
      <p className="text-[9px] tracking-[0.18em] uppercase mb-2" style={{ color: GOLD }}>
        New Alteration Note
      </p>

      <label className="block text-[9.5px] tracking-[0.10em] uppercase mb-1" style={{ color: MUTED }}>Type</label>
      <select data-testid="alt-type" value={type} onChange={(e) => setType(e.target.value)}
        className="w-full mb-2 py-1.5 px-2 text-[12px] bg-white border focus:outline-none"
        style={{ borderColor: LINE, color: INK }}>
        {ALTERATION_TYPES.map((o) => (
          <option key={o.v} value={o.v}>{o.l}</option>
        ))}
      </select>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className="block text-[9.5px] tracking-[0.10em] uppercase mb-1" style={{ color: MUTED }}>
            Delta (cm)
          </label>
          <input data-testid="alt-delta" type="text" inputMode="numeric" pattern="-?[0-9]*"
            value={delta}
            onChange={(e) => setDelta(digitsAndSign(e.target.value))}
            placeholder="e.g. -2"
            className="w-full py-1.5 px-2 text-[12px] bg-white border focus:outline-none"
            style={{ borderColor: LINE, color: INK, fontFamily: 'Playfair Display, serif' }} />
        </div>
        <div>
          <label className="block text-[9.5px] tracking-[0.10em] uppercase mb-1" style={{ color: MUTED }}>
            Note (optional)
          </label>
          <input data-testid="alt-note" type="text" maxLength={120}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Customer prefers…"
            className="w-full py-1.5 px-2 text-[12px] bg-white border focus:outline-none"
            style={{ borderColor: LINE, color: INK }} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button data-testid="alt-cancel" onClick={() => setOpen(false)}
          className="flex-1 py-2 text-[10px] tracking-[0.12em] uppercase border"
          style={{ borderColor: LINE, color: MUTED }}>
          Cancel
        </button>
        <button data-testid="alt-save" onClick={submit} disabled={saving}
          className="flex-1 py-2 text-[10px] tracking-[0.12em] uppercase text-white flex items-center justify-center gap-1 disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #E5B2B9, #D4AF37)' }}>
          {saving ? <Loader2 className="w-3 h-3 animate-spin" strokeWidth={1.6} /> : <CheckCircle className="w-3 h-3" strokeWidth={1.8} />}
          {saving ? 'Saving' : 'Save Note'}
        </button>
      </div>

      {savedAt && (
        <p className="text-[10px] mt-2 flex items-center gap-1" style={{ color: GREEN }}>
          <ShieldCheck className="w-3 h-3" strokeWidth={1.6} /> Saved to customer memory
        </p>
      )}
    </div>
  );
};

const CustomerHistoryPanel = ({ customerId, currentOrderId, expert = false }) => {
  const [open, setOpen] = useState(expert);   // auto-expand for expert tailors
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true); setError(null);
    try {
      const res = await tailorAPI.customerHistory(customerId);
      setData(res);
    } catch (e) {
      setError(e.message || 'Could not load customer memory');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Lazy load on first expand
  useEffect(() => {
    if (open && !data && !loading) load();
  }, [open, data, loading, load]);

  if (!customerId) return null;

  return (
    <div data-testid="customer-history-panel" className="mb-3">
      <button
        data-testid="customer-history-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center justify-between"
        style={{
          backgroundColor: open ? '#FFF' : SOFT,
          border: `1px solid ${open ? GOLD : LINE}`,
        }}>
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" style={{ color: GOLD }} strokeWidth={1.6} />
          <div className="text-left">
            <p className="text-[9px] tracking-[0.18em] uppercase" style={{ color: GOLD }}>
              Customer Memory
            </p>
            <p className="text-[11.5px]" style={{ color: INK, fontWeight: 500 }}>
              {data?.customer_name ? `${data.customer_name}'s tailoring history` : 'Tailoring history & alteration memory'}
            </p>
          </div>
        </div>
        {open
          ? <ChevronUp  className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />
          : <ChevronDown className="w-4 h-4" style={{ color: MUTED }} strokeWidth={1.5} />}
      </button>

      {open && (
        <div className="px-3 py-3 border-t-0 border" style={{ borderColor: GOLD, backgroundColor: '#FFF' }}>
          {loading && (
            <div className="flex items-center gap-2 py-3" data-testid="history-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: GOLD }} strokeWidth={1.6} />
              <span className="text-[11px]" style={{ color: MUTED }}>Loading customer memory…</span>
            </div>
          )}

          {error && (
            <div className="py-3" data-testid="history-error">
              <p className="text-[11px]" style={{ color: '#991B1B' }}>{error}</p>
              <button onClick={load}
                className="mt-1.5 text-[10px] tracking-[0.12em] uppercase underline"
                style={{ color: GOLD }}>
                Retry
              </button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <FitMemoryCard memory={data.fit_memory} />
              <OrderTimeline orders={data.orders} currentOrderId={currentOrderId} />
              <SnapshotPills snapshots={data.snapshots_by_category} />
              <AlterationStream alterations={data.alterations} />
              {currentOrderId && (
                <AddAlterationForm orderId={currentOrderId} onSaved={load} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerHistoryPanel;
